import { MessageSquare, Settings, Megaphone, Filter, TrendingUp } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useState, useCallback, useEffect, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";

const steps = [
  { num: 1, icon: MessageSquare, title: "Discovery Call", copy: "We map out your current system and find exactly where your biggest growth opportunities are." },
  { num: 2, icon: Settings, title: "Pipeline Build", copy: "We set up your CRM, your automations, and your intake flow. You show up for one onboarding call. We handle everything else." },
  { num: 3, icon: Megaphone, title: "Ad Campaign Launch", copy: "We write the ads, set the targeting, and launch your campaign — built for immigration firms in your market." },
  { num: 4, icon: Filter, title: "Lead Qualification", copy: "Every lead is qualified before it reaches you — so your phone rings with people who are ready to hire." },
  { num: 5, icon: TrendingUp, title: "Weekly Optimization", copy: "We watch the numbers every week and keep improving. Your cost per lead goes down. Your results go up." },
];

const cardInitial = { opacity: 0, y: 30 };
const cardAnimate = { opacity: 1, y: 0 };

export function Process() {
  const reduced = useReducedMotion();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: 1,
    dragFree: false,
    watchDrag: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const transitions = useMemo(
    () => steps.map((_, i) => ({ delay: i * 0.1, duration: 0.4 })),
    []
  );

  return (
    <section id="how-it-works" className="py-8 md:py-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">How It Works</h2>
          <p className="mt-2 text-lg text-muted-foreground">Five steps. One system. More clients.</p>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {steps.map((step, i) => (
              <div key={step.num} className="flex-[0_0_85%] sm:flex-[0_0_48%] lg:flex-[0_0_38%] min-w-0">
                <motion.div
                  initial={reduced ? {} : cardInitial}
                  whileInView={reduced ? {} : cardAnimate}
                  viewport={{ once: true }}
                  transition={reduced ? {} : transitions[i]}
                  className="border border-border rounded-2xl p-6 bg-card h-full will-change-transform"
                >
                  <div className="h-1 w-12 bg-primary rounded-full mb-4" />
                  <span className="text-4xl font-bold text-primary/20">{String(step.num).padStart(2, "0")}</span>
                  <div className="flex items-center gap-2 mt-3 mb-2">
                    <step.icon className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-foreground">{step.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.copy}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors"
            aria-label="Previous"
          >
            ←
          </button>
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`rounded-full transition-all duration-200 ${
                  selectedIndex === i
                    ? "w-3 h-3 bg-primary scale-125"
                    : "w-2 h-2 bg-muted-foreground/30"
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors"
            aria-label="Next"
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
}
